import type { SidebarInputMode } from '@/lib/chat/types';
import type { ChatStatus, UIMessage as Message } from 'ai';
import { create } from 'zustand';

import { generateUUID } from '@/lib/utils';

export type PendingSidebarChatPrompt = string | { text: string; modelId: string };

type ScopedChatState = {
  chatId: string | null;
  isChatPersisted: boolean;
  status: ChatStatus;
  codeSessionId?: string | null;
};

type ChatStore = {
  chatId: string | null;
  chatScopeKey: string | null;
  chatByScope: Record<string, ScopedChatState>;
  messages: Message[];
  status: ChatStatus;
  /**
   * Whether the chat has been persisted to the database.
   * False for new chats that haven't sent their first message yet.
   */
  isChatPersisted: boolean;
  /**
   * The code session a pull request page's chat runs on, where it runs on one
   * (`src/components/chat/code-session/`): undefined until the page knows it,
   * which takes the person's latest session on the pull request, and null once
   * they start a new chat, until their first message starts its session. A
   * chat reset by "Ask AI" keeps it: the question goes to the same session.
   */
  codeSessionId: string | null | undefined;
  /**
   * The currently selected text that will be used as input for the chat.
   * This is formatted with separators for visual clarity in the chat.
   */
  selectedText: string;
  selectedTextMode: SidebarInputMode;
  /**
   * Oldest-first queue of external "Ask AI" / draft-comment handoffs and later
   * sends held inside the chat while its engine or saved history is not ready.
   * The mounted chat hands them off for sending or queuing, instead of only
   * prefilling the input like `selectedText`. A list keeps multiple pending sends.
   * `modelId` pins the selected model captured with the prompt; plain strings
   * resolve the current selection when handed off for sending or queuing.
   */
  pendingPrompts: PendingSidebarChatPrompt[];
  /**
   * Switches to the chat of a scope, the pull request's id: the one this page already has there,
   * else the one `takeChatId` gives a scope new to the page, else a new one. Null leaves every
   * scope for a new chat.
   */
  ensureChatScope: (scopeKey: string | null, takeChatId?: () => string | null) => void;
  setChatId: (id: string | null) => void;
  setMessages: (messages: Message[]) => void;
  setStatus: (status: ChatStatus) => void;
  setChatPersisted: (persisted: boolean) => void;
  /**
   * Sets the selected text in the chat input with proper formatting.
   * Used when:
   * 1. User selects text and clicks the "Chat" popup button
   * 2. User selects text and uses Cmd+L keyboard shortcut
   * The text will be formatted with newlines and a separator for visual clarity.
   */
  setSelectedTextAsInput: (text: string, mode?: SidebarInputMode) => void;
  clearSelectedText: () => void;
  addPendingPrompt: (text: string, modelId?: string) => void;
  /** Returns the pending prompts and clears them, so each prompt is sent exactly once. */
  takePendingPrompts: () => PendingSidebarChatPrompt[];
  resetChat: () => void;
  setCodeSessionId: (id: string | null, originatingChatId: string | null) => void;
  /** Starts a new chat, as `/new` does: a page chat on a code session also leaves its session for a new one. */
  startNewChat: () => void;
};

export const useChatStore = create<ChatStore>((set, get) => ({
  chatId: generateUUID(),
  chatScopeKey: null,
  chatByScope: {},
  messages: [],
  status: 'ready',
  isChatPersisted: false,
  codeSessionId: undefined,
  selectedText: '',
  selectedTextMode: 'replace',
  pendingPrompts: [],
  ensureChatScope: (scopeKey, takeChatId) =>
    set((state) => {
      if (state.chatScopeKey === scopeKey) {
        return state;
      }

      if (!scopeKey) {
        return {
          chatId: generateUUID(),
          chatScopeKey: null,
          messages: [],
          status: 'ready',
          isChatPersisted: false,
          codeSessionId: undefined,
          selectedText: '',
          selectedTextMode: 'replace',
          pendingPrompts: [],
        };
      }

      const scopedChat = state.chatByScope[scopeKey] ?? {
        // Taken only for a scope new to the page, which is the only one that would use it.
        chatId: takeChatId?.() ?? generateUUID(),
        isChatPersisted: false,
        status: 'ready' as const,
      };

      return {
        chatScopeKey: scopeKey,
        chatId: scopedChat.chatId,
        isChatPersisted: scopedChat.isChatPersisted,
        status: scopedChat.status,
        codeSessionId: scopedChat.codeSessionId,
        chatByScope: {
          ...state.chatByScope,
          [scopeKey]: scopedChat,
        },
        messages: [],
        selectedText: '',
        selectedTextMode: 'replace',
        pendingPrompts: [],
      };
    }),
  setChatId: (id) => set((state) => updateScopedChatState(state, { chatId: id })),
  setMessages: (messages) => set({ messages }),
  setStatus: (status) => set((state) => updateScopedChatState(state, { status })),
  setChatPersisted: (persisted) =>
    set((state) => updateScopedChatState(state, { isChatPersisted: persisted })),
  setSelectedTextAsInput: (text, mode = 'replace') =>
    set({
      selectedText: text ? `${text}\n\n----\n\n` : '',
      selectedTextMode: mode,
    }),
  clearSelectedText: () => set({ selectedText: '', selectedTextMode: 'replace' }),
  addPendingPrompt: (text, modelId) =>
    set((state) => ({
      pendingPrompts: [...state.pendingPrompts, modelId === undefined ? text : { text, modelId }],
    })),
  takePendingPrompts: () => {
    const { pendingPrompts } = get();
    if (pendingPrompts.length > 0) {
      set({ pendingPrompts: [] });
    }
    return pendingPrompts;
  },
  resetChat: () => set((state) => resetChatState(state, state.codeSessionId)),
  setCodeSessionId: (id, originatingChatId) =>
    set((state) => {
      if (state.chatId !== originatingChatId) {
        return state;
      }
      return updateScopedChatState(state, { codeSessionId: id });
    }),
  startNewChat: () => set((state) => resetChatState(state, null)),
}));

function updateScopedChatState(
  state: ChatStore,
  updates: Partial<ScopedChatState>,
): Partial<ChatStore> {
  const scopedChat: ScopedChatState = {
    chatId: state.chatId,
    isChatPersisted: state.isChatPersisted,
    status: state.status,
    codeSessionId: state.codeSessionId,
    ...updates,
  };

  return {
    ...scopedChat,
    chatByScope:
      state.chatScopeKey && ('chatId' in updates || state.chatId)
        ? {
            ...state.chatByScope,
            [state.chatScopeKey]: scopedChat,
          }
        : state.chatByScope,
  };
}

/** A new chat in the current scope, on `codeSessionId`. */
function resetChatState(
  state: ChatStore,
  codeSessionId: string | null | undefined,
): Partial<ChatStore> {
  return {
    ...updateScopedChatState(state, {
      chatId: generateUUID(),
      isChatPersisted: false,
      status: 'ready',
      codeSessionId,
    }),
    messages: [],
    selectedText: '',
    selectedTextMode: 'replace',
    pendingPrompts: [],
  };
}
